import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Photo, HivTestService } from '../../api/v1/api_pb';
import { useClient } from '../../connect_api';

export default function PhotoCarouselPage() {
    const { id: machineCode } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const client = useClient(HivTestService);

    // Initialize from location state if available
    const [photos, setPhotos] = useState<Photo[]>(location.state?.photos as Photo[] || []);
    const [machineName, setMachineName] = useState<string>(location.state?.machineName as string || '');
    const [isLoadingMachine, setIsLoadingMachine] = useState(false);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isImageLoading, setIsImageLoading] = useState(true);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);

    const handleClose = useCallback(() => {
        navigate(`/machine-detail/${machineCode}`);
    }, [navigate, machineCode]);

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
        setIsImageLoading(true);
    }, [photos.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
        setIsImageLoading(true);
    }, [photos.length]);

    // Swipe gesture handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        if (!e.targetTouches || !e.targetTouches[0]) return;
        setTouchEnd(0); // Reset touchEnd
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (!e.targetTouches || !e.targetTouches[0]) return;
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        } else if (isRightSwipe) {
            handlePrevious();
        }
    };

    // Fallback: Fetch machine data if not available in location state (fixes iPhone Safari issue)
    useEffect(() => {
        const fetchMachineData = async () => {
            // Only fetch if we don't have photos from location state and we have a machine code
            if ((!photos || photos.length === 0) && machineCode && !isLoadingMachine) {
                setIsLoadingMachine(true);
                try {
                    const response = await client.findVendingMachine({ machineCode });
                    if (response.vendingMachine) {
                        setPhotos(response.vendingMachine.photos || []);
                        setMachineName(response.vendingMachine.name || '');
                    }
                } catch (error) {
                    console.error('Failed to fetch vending machine data:', error);
                } finally {
                    setIsLoadingMachine(false);
                }
            }
        };

        fetchMachineData();
    }, [machineCode, client, photos, isLoadingMachine]);

    // Prevent context menu on long press
    const preventContextMenu = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'Escape':
                    handleClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleClose, handlePrevious, handleNext]);

    // Show loading state while fetching machine data
    if (isLoadingMachine) {
        return (
            <div className="photo-carousel-container">
                <div className="carousel-header">
                    <button className="close-button" onClick={handleClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div className="carousel-content">
                    <div className="photo-loading">
                        <div className="spinner"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Handle empty photos array (only show after loading attempt)
    if (!photos || photos.length === 0) {
        return (
            <div className="photo-carousel-container">
                <div className="carousel-header">
                    <button className="close-button" onClick={handleClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div className="carousel-content">
                    <p style={{ color: 'white', fontSize: '18px' }}>No photos available</p>
                </div>
            </div>
        );
    }

    const currentPhoto = photos[currentIndex];

    return (
        <div className="photo-carousel-container">
            {/* Header with close button */}
            <div className="carousel-header">
                <div className="carousel-title">
                    <h2>{machineName}</h2>
                </div>
                <button
                    className="close-button"
                    onClick={handleClose}
                    aria-label="Close carousel"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>

            {/* Main carousel content */}
            <div
                className="carousel-content"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onContextMenu={preventContextMenu}
            >
                {/* Previous button */}
                {photos.length > 1 && (
                    <button
                        className="carousel-nav-button carousel-prev"
                        onClick={handlePrevious}
                        aria-label="Previous photo"
                    >
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <circle cx="20" cy="20" r="20" fill="rgba(255, 255, 255, 0.2)"/>
                            <path d="M24 14L16 20L24 26" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                )}

                {/* Photo display */}
                <div className="carousel-photo-wrapper">
                    {isImageLoading && (
                        <div className="photo-loading">
                            <div className="spinner"></div>
                        </div>
                    )}
                    <img
                        src={currentPhoto.url}
                        alt={`Vending machine ${currentIndex + 1}`}
                        className="carousel-photo"
                        onLoad={() => setIsImageLoading(false)}
                        onError={() => setIsImageLoading(false)}
                        onDragStart={(e) => e.preventDefault()}
                        draggable={false}
                        style={{ display: isImageLoading ? 'none' : 'block' }}
                    />
                </div>

                {/* Next button */}
                {photos.length > 1 && (
                    <button
                        className="carousel-nav-button carousel-next"
                        onClick={handleNext}
                        aria-label="Next photo"
                    >
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <circle cx="20" cy="20" r="20" fill="rgba(255, 255, 255, 0.2)"/>
                            <path d="M16 14L24 20L16 26" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                )}
            </div>

            {/* Photo counter */}
            {photos.length > 1 && (
                <div className="carousel-counter">
                    <span>{currentIndex + 1} / {photos.length}</span>
                </div>
            )}
        </div>
    );
}
